import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Alert, Box, Button, Center, Grid, Heading, Spinner, Stack, Text } from '@chakra-ui/react';
import { canProrateUpgradeToProduct, getUpgradeableSubscriptions, membershipAlreadyCovers, preferredUpgradeSubscriptionId, subscriptionProductId, subscriptionTermId } from '@src/components/sco/UpgradeForm/membershipLadder';
import type { ScoSubscriptionChangeQuote } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import tracker from '@src/lib/tracker';
import moment from 'moment';
import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { useCookie } from 'react-use';
import { CheckoutProductAside } from '../CheckoutProductAside';
import { ConversionCompletion } from '../ConversionCompletion';
import { ConversionUpgradeReview } from '../ConversionUpgradeReview';
import { PaywallOfferControls } from '../PaywallOfferControls';
import { ADVISOR_TEAM_PRODUCT_ID, STARTUPS_PRO_PRODUCT_ID, type PaywallIntentId } from '../intent';
import { billingTermChangeLabel } from '../offers';
import { productLabel, termChangeDirection } from '../presentation';
import { ConversionPaymentStep } from './ConversionPaymentStep';
import { readConversionCheckoutParams, resolveCheckoutCouponCode } from './checkoutParams';
import { useConversionProducts } from './useConversionProducts';
export const SUBSCRIPTION_CHANGE_QUOTE_QUERY = gql`
  query ConversionSubscriptionChangeQuote(
    $subscriptionId: String!
    $productId: String!
    $termId: String
    $couponCode: String
  ) {
    scoSubscriptionChangeQuote(
      subscriptionId: $subscriptionId
      productId: $productId
      termId: $termId
      couponCode: $couponCode
    ) {
      id
      dueNowInCents
      creditsAppliedInCents
      addedToNextInvoiceInCents
      nextBillingAt
      nextBillingAmountInCents
    }
  }
`;
export const CHANGE_SUBSCRIPTION_PRODUCT_MUTATION = gql`
  mutation ConversionChangeSubscriptionScoProduct($input: ChangeSubscriptionScoProductInput!) {
    changeSubscriptionScoProduct(input: $input) {
      subscription {
        id
        subscriptionId
        productId
        product {
          id
          productId
        }
      }
    }
  }
`;
type Step = 'checkout' | 'complete';

/** Memberships renew until canceled from billing, so "Cancel anytime" is true for these alone. */
const MEMBERSHIP_PRODUCT_IDS: string[] = [STARTUPS_PRO_PRODUCT_ID, ADVISOR_TEAM_PRODUCT_ID];
export type ConversionCheckoutProps = {
  onClose: () => void;
  /** Product when the URL has none (legacy `?prompt=subscribe` defaults to Startups Pro, `upgrade` to Advisor Team). */
  defaultProductId?: string;
  /** Intent when the URL has none. */
  defaultIntent?: PaywallIntentId;
};

/**
 * The Conversion checkout (`?prompt=checkout&productId=…`). Sells exactly the product in the
 * URL: a prorated change for members on a membership plan (quoted before it is applied), the
 * existing Stripe checkout for everyone else (guests register inside it). Access is confirmed by
 * refetching the viewer before the completion step.
 */
export const ConversionCheckout = ({
  defaultIntent,
  defaultProductId,
  onClose
}: ConversionCheckoutProps) => {
  const params = useSearchParams();
  const parsed = readConversionCheckoutParams(params);
  const request = {
    ...parsed,
    productId: parsed.productId || defaultProductId || null,
    intent: parsed.intent || defaultIntent || null
  };
  const {
    currentUser,
    currentUserLoading,
    refetchCurrentUser
  } = useCurrentUser();
  const [promoCookie] = useCookie('sco_promo');
  const [step, setStep] = useState<Step>('checkout');
  const initialCouponCode = resolveCheckoutCouponCode({
    params,
    defaultCouponCode: currentUser?.defaultCouponCode,
    promoCookie
  });
  // `undefined` = follow the resolved precedence; `null` = the member removed the code.
  const [enteredCouponCode, setEnteredCouponCode] = useState<string | null | undefined>(undefined);
  const couponCode = enteredCouponCode === undefined ? initialCouponCode : enteredCouponCode;
  const termId = request.termId || 'month';
  const {
    products,
    loading: productLoading,
    error: productError
  } = useConversionProducts([request.productId], {
    termId,
    couponCode
  });
  const product = products[0];
  const appliedCouponCode = product?.appliedCoupon?.code || null;
  const couponError = enteredCouponCode && !productLoading && product && !appliedCouponCode ? `That code doesn't apply to ${productLabel(product)}.` : null;

  // Same product and term is already paid. A different term on that product is a change.
  const alreadyIncluded = membershipAlreadyCovers(currentUser, request.productId, termId);
  const upgradeInPlace = !alreadyIncluded && Boolean(currentUser?.isRegistered) && canProrateUpgradeToProduct(currentUser, request.productId, termId);
  if (!request.productId) {
    return <CheckoutMessage heading={'Choose a plan'} onClose={onClose}>
        This checkout link is missing its plan.
      </CheckoutMessage>;
  }
  if (step === 'complete') {
    return <DialogPane>
        <ConversionCompletion variant={'plain'} onReturn={onClose} />
      </DialogPane>;
  }
  if (currentUserLoading || productLoading && !product) {
    return <Center maxW={'full'} minH={'20rem'} w={'28rem'}>
        <Spinner aria-label={'Loading checkout'} />
      </Center>;
  }
  if (productError || !product) {
    return <CheckoutMessage heading={'This plan isn’t available right now'} onClose={onClose}>
        Please try again in a moment, or contact support@startups.com.
      </CheckoutMessage>;
  }
  if (alreadyIncluded) {
    return <DialogPane>
        <ConversionCompletion detail={`Your current plan already includes ${productLabel(product)}.`} variant={'plain'} onReturn={onClose} />
      </DialogPane>;
  }
  const complete = async () => {
    try {
      await refetchCurrentUser?.();
    } catch {
      // The completion step still closes back to the page; a later poll refreshes access.
    }
    setStep('complete');
  };
  if (upgradeInPlace) {
    return <UpgradeInPlace couponCode={appliedCouponCode} intent={request.intent} product={product} termId={termId} onComplete={complete} />;
  }
  return <Grid gap={0} maxW={'full'} templateColumns={{
    base: '1fr',
    md: 'minmax(0, 0.9fr) minmax(0, 1.1fr)'
  }} w={{
    base: 'full',
    md: '56rem'
  }}>
      {/* The dialog doesn't clip, so the filled pane takes the dialog's corners on its outer edge. */}
      <Stack bg={'colorPalette.card/25'} borderBottomStartRadius={{
      base: 0,
      md: 'l3'
    }} borderBottomWidth={{
      base: '1px',
      md: 0
    }} borderColor={'colorPalette.border/33'} borderEndWidth={{
      base: 0,
      md: '1px'
    }} borderTopEndRadius={{
      base: 'l3',
      md: 0
    }} borderTopStartRadius={'l3'} p={{
      base: 5,
      md: 8
    }}
    // Stacked, the pane is the dialog's top edge: the headline starts below the close button.
    pt={{
      base: 12,
      md: 8
    }}>
        <CheckoutProductAside assurance={MEMBERSHIP_PRODUCT_IDS.includes(product.productId) ? 'Cancel anytime' : undefined} intent={request.intent || undefined} product={product} tokens={request.tokens || undefined} variant={'plain'} controls={<PaywallOfferControls appliedCouponCode={appliedCouponCode} couponError={couponError} couponIsApplying={Boolean(enteredCouponCode) && productLoading} selectedTermId={termId} terms={[]} onApplyCoupon={code => setEnteredCouponCode(code)} onRemoveCoupon={() => setEnteredCouponCode(null)} onTermChange={() => undefined} />} />
      </Stack>
      {/* Only an API-applied code reaches the charge, so the price shown is the price paid. */}
      <ConversionPaymentStep intent={request.intent} selection={{
      productId: product.productId,
      termId,
      couponCode: appliedCouponCode
    }} onSuccess={complete} />
    </Grid>;
};
type UpgradeInPlaceProps = {
  product: NonNullable<ReturnType<typeof useConversionProducts>['products'][number]>;
  termId: string;
  couponCode: string | null;
  intent?: string | null;
  onComplete: () => Promise<void>;
};
const UpgradeInPlace = ({
  couponCode,
  intent,
  onComplete,
  product,
  termId
}: UpgradeInPlaceProps) => {
  const {
    currentUser
  } = useCurrentUser();
  const subscriptions = useMemo(() => getUpgradeableSubscriptions(currentUser), [currentUser]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const subscriptionId = selectedId || preferredUpgradeSubscriptionId(currentUser, product.productId, termId);
  const changing = subscriptions.find(subscription => subscription.subscriptionId === subscriptionId);
  const heldTerm = changing && subscriptionProductId(changing) === product.productId ? subscriptionTermId(changing) : null;
  const direction = heldTerm ? termChangeDirection(heldTerm, termId) : null;
  const [changeError, setChangeError] = useState<string | null>(null);
  const quoteQuery = useQuery(SUBSCRIPTION_CHANGE_QUOTE_QUERY, {
    skip: !subscriptionId,
    fetchPolicy: 'network-only',
    variables: {
      subscriptionId: subscriptionId || '',
      productId: product.productId,
      termId,
      couponCode: couponCode || undefined
    }
  });
  const quote = (quoteQuery.data?.scoSubscriptionChangeQuote || null) as ScoSubscriptionChangeQuote | null;
  const [changeSubscription, changeState] = useMutation(CHANGE_SUBSCRIPTION_PRODUCT_MUTATION);

  // The purchase path's payment form tracks this itself; the upgrade path never renders it.
  useEffect(() => {
    tracker.checkoutStarted({
      product,
      intent
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.productId, termId]);
  const options = subscriptions.map(subscription => ({
    id: subscription.subscriptionId,
    label: `${subscription.product?.name || subscription.productId} · started ${moment(subscription.activatedAt).format('ll')}`,
    nextAssessmentAt: subscription.nextAssessmentAt
  }));
  return <DialogPane>
      <ConversionUpgradeReview error={changeError} loading={changeState.loading} quote={quote} quoteError={quoteQuery.error ? 'We couldn’t price this change right now.' : null} quoteLoading={quoteQuery.loading} selectedSubscriptionId={subscriptionId || undefined} subscriptions={options} targetProduct={product} title={direction ? billingTermChangeLabel(direction, termId) : undefined} variant={'plain'} confirmLabel={direction === 'downgrade' ? 'Downgrade now' : direction === 'switch' ? 'Confirm change' : 'Upgrade now'} onRetryQuote={() => quoteQuery.refetch()} onSelectSubscription={setSelectedId} onContinue={async selection => {
      setChangeError(null);
      try {
        await changeSubscription({
          variables: {
            input: {
              subscriptionId: selection.subscriptionId,
              productId: product.productId,
              termId,
              couponCode: couponCode || undefined,
              doProratedUpgrade: true
            }
          }
        });
      } catch (error) {
        setChangeError((error as Error)?.message || 'We could not change your plan. Please try again.');
        return;
      }
      await onComplete();
    }} />
    </DialogPane>;
};

/** A single-column dialog step: the dialog is the surface, so its content needs no card of its own. */
const DialogPane = t0 => {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      base: 6,
      md: 8
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <Box maxW="full" p={t1} w="28rem">{children}</Box>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const CheckoutMessage = t0 => {
  const $ = _c(12);
  const {
    children,
    heading,
    onClose
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      base: 6,
      md: 8
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== heading) {
    t2 = <Heading size="lg">{heading}</Heading>;
    $[1] = heading;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Alert.Indicator />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== children) {
    t4 = <Alert.Root status="info">{t3}<Text>{children}</Text></Alert.Root>;
    $[4] = children;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== onClose) {
    t5 = <Button alignSelf="flex-start" variant="outline" onClick={onClose}>Close</Button>;
    $[6] = onClose;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t2 || $[9] !== t4 || $[10] !== t5) {
    t6 = <Stack gap={4} maxW="full" p={t1} w="28rem">{t2}{t4}{t5}</Stack>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
};